import type { FC, RefObject } from 'react';
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CaseCardBase } from '../cases/CaseCardBase';
import { cn } from '../../utils/cn';
import type { CaseBattleRoundResponse } from '../../services/generated/Api';

interface CaseStripProps {
  rounds: CaseBattleRoundResponse[];
  currentRound: number | null;
  dropColor: string | null;
  currentCaseRef: RefObject<HTMLSpanElement | null>;
  size?: number;
}

export const CaseStrip: FC<CaseStripProps> = memo(function CaseStrip({ rounds, currentRound, dropColor, currentCaseRef, size = 112 }) {
  const stripRef = useRef<HTMLDivElement>(null);

  const [scrollHint, setScrollHint] = useState({ start: false, end: false });
  const syncScrollHint = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const scrolled = Math.abs(el.scrollLeft);
    setScrollHint({ start: scrolled > 1, end: scrolled + el.clientWidth < el.scrollWidth - 1 });
  }, []);

  useEffect(() => {
    syncScrollHint();
    const el = stripRef.current;
    if (!el) return;
    const observer = new ResizeObserver(syncScrollHint);
    observer.observe(el);
    return () => observer.disconnect();
  }, [syncScrollHint, rounds.length]);

  const scrolledRef = useRef(false);
  useLayoutEffect(() => {
    const el = stripRef.current;
    const card = currentCaseRef.current;
    if (!el || !card) return;
    const left = Math.max(0, card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2);
    el.scrollTo({ left, behavior: scrolledRef.current ? 'smooth' : 'instant' });
    scrolledRef.current = true;
  }, [currentRound, currentCaseRef]);

  return (
    <div className="relative">
      <div
        ref={stripRef}
        onScroll={syncScrollHint}
        className="relative flex gap-2 overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] -mx-1 px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {rounds.map((round, index) => {
          const played = currentRound !== null && index < currentRound;
          const current = currentRound === index;
          return (
            <span
              key={round.id}
              ref={current ? currentCaseRef : null}
              className={cn('relative shrink-0 rounded-2xl transition-opacity duration-500', played && 'opacity-40')}
            >
              <CaseCardBase
                imageUrl={round.caseImageUrl}
                imagePlaceholder={round.caseImagePlaceholder}
                alt={round.caseName}
                className="rounded-2xl"
                style={{ width: size, height: size }}
              />

              {current && (
                <span
                  key={dropColor ?? 'live'}
                  className={cn(
                    'pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-inset [animation:shotBattleRingIn_320ms_ease-out_both]',
                    dropColor === null && 'ring-white/70',
                  )}
                  style={dropColor === null ? undefined : { '--tw-ring-color': dropColor, boxShadow: `inset 0 0 0 3px ${dropColor}, inset 0 0 44px ${dropColor}cc` }}
                />
              )}
            </span>
          );
        })}
      </div>

      {scrollHint.start && (
        <div className="pointer-events-none absolute inset-y-0 -start-1 w-6 bg-gradient-to-l rtl:bg-gradient-to-r from-transparent to-bg-secondary" />
      )}
      {scrollHint.end && (
        <div className="pointer-events-none absolute inset-y-0 -end-1 w-6 bg-gradient-to-r rtl:bg-gradient-to-l from-transparent to-bg-secondary" />
      )}
    </div>
  );
});
